import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import {
  ArrowRight,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Cloud,
  Factory,
  FolderKanban,
  GitBranch,
  Heart,
  Landmark,
  Layers,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Receipt,
  Rocket,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sprout,
  Store,
  Target,
  Tags,
  Truck,
  Users,
  Wallet,
  WifiOff,
  Workflow,
  Boxes,
  CreditCard,
  Briefcase,
  Database,
  Eye,
} from "lucide-react";
import { SiteLayout, Section, SectionHeading } from "@/components/site/Layout";
import { ClientsMarquee } from "@/components/site/ClientsMarquee";
import dashboardHero from "@/assets/dashboard-hero.jpg";
import kijaProduct from "@/assets/kija-product.jpg";
import caseAgrocrown from "@/assets/case-agrocrown.jpg";
import astraHero from "@/assets/astra-hero.png";
import caseAgrochemical from "@/assets/astra-erp-mockup.jpg";

export function HomePage() {
  const services = [
    { icon: Layers, title: "ERP Systems Development", desc: "End-to-end enterprise systems for managing business operations." },
    { icon: Workflow, title: "Business Process Automation", desc: "Replace manual workflows with structured digital systems." },
    { icon: Factory, title: "Manufacturing Systems", desc: "Production tracking, inventory and factory operations." },
    { icon: Wallet, title: "Finance & Treasury", desc: "Cashflow, budgeting, payments and reporting tools." },
    { icon: Users, title: "HR & Workforce", desc: "Attendance, payroll and employee management." },
    { icon: Building2, title: "Enterprise systems, end to end", desc: "Complete enterprise-grade solutions tailored to your business." },
  ];

  const why = [
    { icon: Factory, title: "Built from real operations", desc: "Designed inside live factories and finance teams, not from a textbook." },
    { icon: Sprout, title: "Made for African business", desc: "Solves the realities of local procurement, cash, and compliance." },
    { icon: Layers, title: "Modular & scalable", desc: "Start with one module, grow into a full ERP, without rewrites." },
    { icon: ShieldCheck, title: "Secure by default", desc: "Role-based access, audit trails and encrypted data." },
    { icon: Cloud, title: "Cloud-ready deployment", desc: "Deploy on your servers or in the cloud, your choice." },
    { icon: Rocket, title: "Fast to deploy", desc: "Go live in weeks, not months, with minimal disruption." },
  ];

  const industries = [
    { icon: Factory, label: "Manufacturing & Production" },
    { icon: Sprout, label: "Agriculture & Agro-processing" },
    { icon: Store, label: "Retail & Distribution" },
    { icon: Landmark, label: "Financial Services" },
    { icon: Building2, label: "Government & Public Sector" },
    { icon: Briefcase, label: "SMEs & Startups" },
  ];

  const steps = [
    { n: "01", title: "Discovery", desc: "We sit with your team and map your operations end to end." },
    { n: "02", title: "System Design", desc: "We architect ERP modules tailored to your workflows." },
    { n: "03", title: "Deployment", desc: "We deploy, integrate and migrate your data safely." },
    { n: "04", title: "Support & Scaling", desc: "We maintain, train and grow the system with you." },
  ];

  return (
    <SiteLayout>
      <section className="relative overflow-hidden" style={{ background: "var(--gradient-hero)" }}>
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_20%,white,transparent_40%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-12 lg:pt-28 lg:pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
              Build. Automate. <span className="text-[var(--brand-green)]">Scale your business.</span>
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-xl">
              Zenisoft Ghana develops ERP systems that help businesses manage finance, inventory, HR and operations, all in one platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/contact" className="inline-flex items-center gap-2 rounded-md bg-[var(--brand-green)] px-5 py-3 text-sm font-semibold text-[var(--brand-navy-deep)] hover:opacity-90 transition">
                <Rocket className="h-4 w-4" /> Request Demo
              </a>
              <a href="/contact" className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
                <Calendar className="h-4 w-4" /> Book Consultation
              </a>
            </div>
          </div>
          <a href="/astra" className="relative block">
            <div className="absolute -inset-4 rounded-2xl bg-[var(--brand-green)]/20 blur-2xl" />
            <img src={dashboardHero} alt="Zenisoft ERP dashboard" width={1600} height={1024} className="relative rounded-xl border border-white/10 shadow-2xl" />
          </a>
        </div>
      </section>

      <section className="border-y border-border bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-sm text-muted-foreground">Built for real business environments across Ghana and Africa.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span>Manufacturing</span><span>Agro-processing</span><span>SMEs</span><span>Finance</span><span>Government</span>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeading eyebrow="What we do" title="Enterprise systems, end to end" description="From the first workflow audit to scaled multi-branch deployment." />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <div key={s.title} className="group rounded-xl border border-border bg-card p-6 hover:border-[var(--brand-green)] hover:shadow-lg transition">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--brand-navy)]/5 text-[var(--brand-navy)] group-hover:bg-[var(--brand-green)]/15 group-hover:text-[var(--brand-navy-deep)] transition">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-foreground">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <a href="/astra" className="order-2 lg:order-1 block">
            <img src={kijaProduct} alt="Astra ERP platform" width={1400} height={960} loading="lazy" className="rounded-xl border border-border shadow-xl" />
          </a>
          <div className="order-1 lg:order-2">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Featured Product</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Astra ERP Platform</h2>
            <p className="mt-4 text-lg text-muted-foreground">All-in-one ERP system built for African businesses.</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {["Inventory Management", "Manufacturing Control", "Finance & Accounting", "HR & Attendance", "Reporting Dashboard", "Multi-branch Support"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-[var(--brand-green)]" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <a href="/astra" className="inline-flex items-center gap-2 rounded-md bg-[var(--brand-navy)] px-5 py-3 text-sm font-semibold text-white hover:opacity-90 transition">
                View Demo System <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeading eyebrow="Why Zenisoft" title="Why businesses choose us" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {why.map((w) => (
            <div key={w.title} className="rounded-xl border border-border p-6">
              <w.icon className="h-6 w-6 text-[var(--brand-green)]" />
              <h3 className="mt-4 font-semibold text-foreground">{w.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{w.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <SectionHeading eyebrow="Industries" title="Industries we serve" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((i) => (
              <div key={i.label} className="flex items-center gap-4 rounded-xl border border-border bg-background p-5 hover:border-[var(--brand-green)] transition">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[var(--brand-navy)] text-white">
                  <i.icon className="h-5 w-5" />
                </div>
                <span className="font-medium">{i.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section>
        <SectionHeading eyebrow="Our process" title="How we work with you" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => (
            <div key={s.n} className="relative rounded-xl border border-border p-6">
              <div className="text-4xl font-bold text-[var(--brand-green)]/30">{s.n}</div>
              <h3 className="mt-3 font-semibold text-foreground">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Industry Solution</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Astra ERP for Agrochemical Manufacturing</h2>
            <p className="mt-4 text-white/70">
              Purpose-built ERP modules for agrochemical manufacturers, from raw material sourcing and batch formulation to quality control, compliance tracking, and distribution.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Raw material & chemical inventory", "Batch production & formula management", "Quality control & lab testing", "Regulatory compliance & traceability", "Distribution & dealer network", "Integrated finance & costing"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[var(--brand-green)]" /> {f}</li>
              ))}
            </ul>
            <a href="/astra" className="mt-8 inline-flex items-center gap-2 rounded-md bg-[var(--brand-green)] px-5 py-3 text-sm font-semibold text-[var(--brand-navy-deep)] hover:opacity-90 transition">
              Explore Astra ERP <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <a href="/astra" className="block">
            <img src={caseAgrochemical} alt="Astra ERP for agrochemical manufacturing" width={1400} height={900} loading="lazy" className="rounded-xl border border-white/10 shadow-2xl" />
          </a>
        </div>
      </section>

      <Section>
        <div className="rounded-2xl border border-border bg-gradient-to-br from-[var(--brand-navy)] to-[var(--brand-navy-deep)] p-10 lg:p-14 text-white grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">
              <ShoppingBag className="h-4 w-4" /> New • Astra POS
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Retail point-of-sale, built for African stores.</h2>
            <p className="mt-4 text-white/70 max-w-2xl">
              Fast checkout, real-time inventory, multi-branch sales and offline-ready operation keeps your retail running, even when the internet does not.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="/astra-pos" className="inline-flex items-center gap-2 rounded-md bg-[var(--brand-green)] px-5 py-3 text-sm font-semibold text-[var(--brand-navy-deep)] hover:opacity-90 transition">
              Explore Astra POS <ArrowRight className="h-4 w-4" />
            </a>
            <a href="/contact" className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
              Request Demo
            </a>
          </div>
        </div>
      </Section>

      <ClientsMarquee />
    </SiteLayout>
  );
}

export function AboutPage() {
  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">About</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">We build ERP systems that power African businesses.</h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">
            Zenisoft Ghana is a software engineering company focused on enterprise systems. We design, build and deploy ERPs that fit the real way African companies operate.
          </p>
        </div>
      </section>

      <Section>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Target, title: "Mission", desc: "To deliver world-class ERP systems that unlock productivity for African enterprises." },
            { icon: Eye, title: "Vision", desc: "An Africa where every business runs on systems built by Africans, for African realities." },
            { icon: Heart, title: "Values", desc: "Craft. Honesty. Long-term partnership. We measure success by the systems still running years later." },
          ].map((v) => (
            <div key={v.title} className="rounded-xl border border-border p-6">
              <v.icon className="h-6 w-6 text-[var(--brand-green)]" />
              <h3 className="mt-4 font-semibold">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{v.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="!pt-0">
        <SectionHeading eyebrow="Our story" title="Built inside real factories, not lecture halls" />
        <div className="prose prose-slate max-w-3xl text-muted-foreground space-y-4">
          <p>Zenisoft started inside the operations of a working factory floor. We watched what slowed teams down, paper trails, broken spreadsheets, disconnected modules, and built systems that fix them.</p>
          <p>Today we work with manufacturers, agro-processors, finance teams, and government partners across Ghana and beyond. Every ERP we ship carries the same DNA: real workflows, real users, real impact.</p>
        </div>
        <div className="mt-10">
          <a href="/contact" className="inline-flex items-center rounded-md bg-[var(--brand-navy)] px-5 py-3 text-sm font-semibold text-white">Talk to our team</a>
        </div>
      </Section>
    </SiteLayout>
  );
}

export function CaseStudiesPage() {
  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Case Studies</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">Real systems, running real businesses.</h1>
        </div>
      </section>
      <Section>
        <article className="grid lg:grid-cols-2 gap-10 items-center rounded-2xl border border-border p-6 lg:p-10">
          <a href="/astra" className="block">
            <img src={caseAgrocrown} alt="Agrocrown" width={1400} height={900} className="rounded-xl border border-border" />
          </a>
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Featured</div>
            <h2 className="text-3xl font-bold tracking-tight">Agrocrown Manufacturing System</h2>
            <p className="mt-3 text-muted-foreground">An agro-processing factory replaced spreadsheets and paper logs with a unified ERP, gaining live inventory, attendance, production and finance visibility.</p>
            <ul className="mt-6 space-y-2 text-sm">
              {["Inventory automation", "Production tracking", "HR attendance system", "Finance integration"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[var(--brand-green)]" /> {f}</li>
              ))}
            </ul>
            <a href="/contact" className="mt-8 inline-flex items-center gap-2 rounded-md bg-[var(--brand-navy)] px-5 py-3 text-sm font-semibold text-white">
              Request a similar system <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </article>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {[
            { t: "Finance Operations — SME", d: "Cashflow, payments and reporting consolidation for a multi-branch SME." },
            { t: "HR & Payroll — Manufacturing", d: "Attendance and payroll automation across 200+ workers." },
          ].map((c) => (
            <div key={c.t} className="rounded-xl border border-border p-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Coming soon</div>
              <h3 className="mt-2 font-semibold">{c.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
      </Section>
    </SiteLayout>
  );
}

export function IndustriesPage() {
  const industries = [
    { icon: Factory, title: "Manufacturing & Production", desc: "Production planning, BOMs, work orders, quality and machine downtime tracking." },
    { icon: Sprout, title: "Agriculture & Agro-processing", desc: "Inputs, harvest tracking, processing, and full supply chain visibility." },
    { icon: Store, title: "Retail & Distribution", desc: "POS, multi-branch inventory, suppliers, deliveries and rep performance." },
    { icon: Landmark, title: "Financial Services", desc: "Loan management, savings, treasury and reporting tailored for non-bank lenders." },
    { icon: Building2, title: "Government & Public Sector", desc: "Asset tracking, procurement and project-level finance for public bodies." },
    { icon: Briefcase, title: "SMEs & Startups", desc: "Lightweight ERP to formalize operations and scale without rebuilds." },
  ];

  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Industries</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">Tailored ERP for every sector we serve</h1>
        </div>
      </section>
      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {industries.map((i) => (
            <div key={i.title} className="rounded-xl border border-border p-6 hover:border-[var(--brand-green)] transition">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--brand-navy)] text-white">
                <i.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-semibold">{i.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{i.desc}</p>
            </div>
          ))}
        </div>
      </Section>
    </SiteLayout>
  );
}

export function SolutionsPage() {
  const solutions = [
    { icon: Layers, title: "ERP Systems Development", desc: "Custom-built enterprise systems covering every department from sales to procurement to production.", features: ["Custom modules", "Multi-branch", "Role-based access", "API integrations"] },
    { icon: Workflow, title: "Business Process Automation", desc: "Turn manual, paper-heavy workflows into structured digital processes with approvals and audit trails.", features: ["Workflow builder", "Approvals", "Notifications", "Audit logs"] },
    { icon: Factory, title: "Manufacturing Systems", desc: "Production planning, work orders, inventory and quality control, the backbone of Astra ERP.", features: ["Production tracking", "Inventory", "BOM management", "Quality control"] },
    { icon: Wallet, title: "Finance & Treasury", desc: "Cashflow, budgeting, payments, taxes and reporting that finance teams actually enjoy using.", features: ["GL & journals", "Cashflow", "Budgets", "Tax reports"] },
    { icon: Users, title: "HR & Workforce", desc: "Attendance, payroll, leave and employee management with mobile-friendly access.", features: ["Attendance", "Payroll", "Leave", "Employee profiles"] },
  ];

  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Solutions</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">ERP solutions that fit your operations</h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">Five core capabilities, deployed as a unified platform or as standalone modules.</p>
        </div>
      </section>
      <Section>
        <div className="grid gap-6 lg:grid-cols-2">
          {solutions.map((s) => (
            <div key={s.title} className="rounded-xl border border-border p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--brand-navy)] text-white">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-muted-foreground">{s.desc}</p>
              <ul className="mt-4 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                {s.features.map((f) => <li key={f}>• {f}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <a href="/contact" className="inline-flex items-center gap-2 rounded-md bg-[var(--brand-navy)] px-5 py-3 text-sm font-semibold text-white">
            Request a tailored proposal <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </Section>
    </SiteLayout>
  );
}

export function AstraPage() {
  const modules = [
    { icon: Wallet, title: "Finance & Accounting", desc: "GL, AP/AR, multi-entity consolidation, tax." },
    { icon: Factory, title: "Manufacturing", desc: "MRP, BOMs, work orders, shop-floor control." },
    { icon: Landmark, title: "Treasury", desc: "Cash, liquidity, FX and risk management." },
    { icon: Truck, title: "Supply Chain", desc: "Procurement, planning and supplier collaboration." },
    { icon: Boxes, title: "Inventory & Warehouse", desc: "Multi-site stock, lot/serial tracking, WMS." },
    { icon: Store, title: "Sales & CRM", desc: "Quotes, orders, pipeline and customer 360." },
    { icon: Users, title: "Human Resources", desc: "People, payroll, time and performance." },
    { icon: FolderKanban, title: "Project Management", desc: "Budgets, resourcing, billing and margins." },
    { icon: BarChart3, title: "Business Intelligence", desc: "Real-time dashboards across every module." },
    { icon: Briefcase, title: "Service & Maintenance", desc: "Field service, assets and SLAs in one place." },
    { icon: Database, title: "Document Management", desc: "Centralised records, approvals and audit trails." },
    { icon: GitBranch, title: "Platform & Integrations", desc: "APIs, workflows and extensibility across the suite." },
  ];

  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Astra ERP</div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Built for businesses that have outgrown disconnected tools.</h1>
            <p className="mt-6 text-lg text-white/70">Replace a dozen spreadsheets, point solutions and aging on-prem systems with one scalable suite built to grow with you.</p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <a href="/contact" className="rounded-md bg-[var(--brand-green)] px-5 py-3 text-sm font-semibold text-[var(--brand-navy-deep)]">Get Started</a>
              <a href="/contact" className="rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-white">Book a Demo</a>
            </div>
          </div>
          <a href="/contact" className="block">
            <img src={kijaProduct} alt="Astra ERP" width={1400} height={960} className="rounded-xl border border-white/10 shadow-2xl" />
          </a>
        </div>
      </section>

      <Section>
        <SectionHeading eyebrow="Why Astra ERP" title="A complete ERP, broken into composable modules" />
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { title: "One source of truth", desc: "Every transaction, every entity, every team connected to the same data model." },
            { title: "Built to scale", desc: "Multi-company, multi-currency, multi-language for your next market." },
            { title: "Fast to deploy", desc: "Modular by design. Start with what you need today." },
          ].map((p) => (
            <div key={p.title} className="rounded-xl border border-border p-6">
              <Layers className="h-6 w-6 text-[var(--brand-green)]" />
              <h3 className="mt-4 font-semibold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="The Suite" title="12 modules. One platform." />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <div key={m.title} className="rounded-xl border border-border p-6">
              <m.icon className="h-6 w-6 text-[var(--brand-green)]" />
              <h3 className="mt-4 font-semibold">{m.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-20">
          <SectionHeading center eyebrow="Industries" title="Built for industry, configured for you" />
          <p className="text-center text-muted-foreground max-w-2xl mx-auto -mt-4 mb-8">Pre-built process flows and KPIs tailored to how your sector actually operates.</p>
          <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 mx-auto max-w-3xl">
            {["Manufacturing", "Distribution", "Services", "Food & Beverage", "Chemicals", "Construction", "Retail", "Financial Services"].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-[var(--brand-green)]" /> {f}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Clock className="h-8 w-8 text-[var(--brand-green)] mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ready to unify your business?</h2>
          <p className="mt-4 text-white/70">Pick a module, see it live, and roll out the rest of Astra ERP at your pace.</p>
          <div className="mt-8 flex justify-center gap-3">
            <a href="/contact" className="rounded-md bg-[var(--brand-green)] px-6 py-3 text-sm font-semibold text-[var(--brand-navy-deep)]">Get Started</a>
            <a href="/contact" className="rounded-md border border-white/20 px-6 py-3 text-sm font-semibold text-white">Book a Demo</a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export function AstraPosPage() {
  const features = [
    { icon: Receipt, title: "Fast checkout", desc: "Barcode scan, split payments, receipts printed or sent via SMS/WhatsApp." },
    { icon: Boxes, title: "Real-time inventory", desc: "Stock levels update instantly across every till and branch." },
    { icon: WifiOff, title: "Offline-ready", desc: "Keep selling when the internet drops and sync later." },
    { icon: Store, title: "Multi-branch", desc: "Run one shop or fifty from a single dashboard." },
    { icon: CreditCard, title: "Mobile money & cards", desc: "Accept MoMo, cards, cash and bank transfers." },
    { icon: Tags, title: "Promotions & loyalty", desc: "Discounts, coupons and customer loyalty points built in." },
    { icon: Users, title: "Staff & shifts", desc: "Cashier logins, shift opening/closing and accountability." },
    { icon: BarChart3, title: "Sales analytics", desc: "Daily, weekly and monthly sales, best-sellers and margin reports." },
    { icon: Smartphone, title: "Runs anywhere", desc: "Works on POS terminals, tablets, laptops and Android devices." },
  ];

  const benefits = [
    "Cut checkout time by up to 60%",
    "Eliminate end-of-day cash mismatches",
    "Stop stock-outs with auto reorder alerts",
    "See live sales from any branch on your phone",
    "Integrates seamlessly with Astra ERP",
  ];

  const industries = ["Supermarkets", "Pharmacies", "Boutiques & Fashion", "Electronics", "Restaurants & QSR", "Hardware Stores", "Mini-marts", "Wholesale & Distribution"];

  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 lg:py-10 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">
              <ShoppingBag className="h-4 w-4" /> Astra POS
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Retail point-of-sale that keeps your store running.</h1>
            <p className="mt-6 text-lg text-white/70">
              Astra POS is a modern, offline-ready point-of-sale built for African retailers from single shops to multi-branch chains.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/contact" className="inline-flex items-center gap-2 rounded-md bg-[var(--brand-green)] px-5 py-3 text-sm font-semibold text-[var(--brand-navy-deep)] hover:opacity-90 transition">
                <Rocket className="h-4 w-4" /> Request Demo
              </a>
              <a href="/contact" className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
                Talk to Sales
              </a>
            </div>
          </div>
          <a href="/contact" className="relative flex justify-center">
            <div className="absolute inset-0 rounded-full bg-[var(--brand-green)]/20 blur-3xl" />
            <img src={astraHero} alt="Astra POS mobile app" className="relative max-h-[560px] w-auto object-contain" />
          </a>
        </div>
      </section>

      <Section>
        <SectionHeading eyebrow="Features" title="Everything you need to run the till" description="Built from real retail floors, not a textbook." />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-xl border border-border p-6 hover:border-[var(--brand-green)] transition">
              <f.icon className="h-6 w-6 text-[var(--brand-green)]" />
              <h3 className="mt-4 font-semibold text-foreground">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading eyebrow="Why Astra POS" title="Run a tighter, faster retail operation" />
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-[var(--brand-green)] mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck, label: "Secure by default", desc: "Role-based access & full audit trail." },
              { icon: WifiOff, label: "Offline mode", desc: "Sells even without internet." },
              { icon: Smartphone, label: "Any device", desc: "Terminal, tablet, laptop or phone." },
              { icon: BarChart3, label: "Live reports", desc: "From any branch, any time." },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-border bg-background p-5">
                <c.icon className="h-6 w-6 text-[var(--brand-green)]" />
                <div className="mt-3 font-semibold text-sm">{c.label}</div>
                <p className="mt-1 text-xs text-muted-foreground">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section>
        <SectionHeading center eyebrow="Industries" title="Made for every kind of store" />
        <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-4 mx-auto max-w-4xl">
          {industries.map((i) => (
            <li key={i} className="flex items-center gap-2 rounded-md border border-border bg-background px-4 py-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-[var(--brand-green)]" /> {i}
            </li>
          ))}
        </ul>
      </Section>

      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <ShoppingBag className="h-8 w-8 text-[var(--brand-green)] mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ready to modernise your checkout?</h2>
          <p className="mt-4 text-white/70">See Astra POS live in your store environment, we’ll set up a tailored demo.</p>
          <div className="mt-8 flex justify-center flex-wrap gap-3">
            <a href="/contact" className="rounded-md bg-[var(--brand-green)] px-6 py-3 text-sm font-semibold text-[var(--brand-navy-deep)]">Request Demo</a>
            <a href="/astra" className="inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3 text-sm font-semibold text-white">
              Explore Astra ERP <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export function ContactPage() {
  const [formLoadedAt, setFormLoadedAt] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormLoadedAt(Date.now());
  }, []);

  const Schema = z.object({
    name: z.string().trim().min(1, "Name is required").max(100, "Name is too long"),
    company: z.string().trim().max(120, "Company is too long").optional().or(z.literal("")),
    email: z.string().trim().email("Enter a valid email").max(255),
    phone: z.string().trim().max(30, "Phone is too long").optional().or(z.literal("")),
    message: z.string().trim().min(1, "Tell us a bit about your needs").max(2000, "Message is too long"),
  });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      company: String(fd.get("company") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      message: String(fd.get("message") ?? ""),
      website: String(fd.get("website") ?? ""),
      ts: formLoadedAt || Date.now(),
    };

    const result = Schema.safeParse(payload);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const [k, v] of Object.entries(result.error.flatten().fieldErrors)) {
        if (v && v[0]) fieldErrors[k] = v[0];
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    const subject = encodeURIComponent(`Website demo request from ${result.data.name}`);
    const body = encodeURIComponent([
      `Name: ${result.data.name}`,
      `Company: ${result.data.company || "-"}`,
      `Email: ${result.data.email}`,
      `Phone: ${result.data.phone || "-"}`,
      "",
      result.data.message,
    ].join("\n"));

    window.location.href = `mailto:business@zenisoft.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setSubmitting(false);
  }

  return (
    <SiteLayout>
      <section className="bg-[var(--brand-navy-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--brand-green)] mb-3">Contact</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">Let's build a system that runs your business.</h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">Tell us about your operations. We'll schedule a free 30-minute consultation.</p>
        </div>
      </section>
      <Section>
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h3 className="font-semibold mb-3">Reach us</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-[var(--brand-green)]" /> Accra, Ghana</li>
                <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-[var(--brand-green)]" /> business@zenisoft.com</li>
                <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-[var(--brand-green)]" /> +233 54 604 4269 | +233 55 434 3921</li>
              </ul>
            </div>
            <a href="https://wa.me/233546044269" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-md bg-[var(--brand-green)] px-5 py-3 text-sm font-semibold text-[var(--brand-navy-deep)]">
              <MessageCircle className="h-4 w-4" /> Message us on WhatsApp
            </a>
          </div>
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="rounded-xl border border-[var(--brand-green)]/30 bg-[var(--brand-green)]/10 p-8 text-center">
                <CheckCircle2 className="h-10 w-10 mx-auto text-[var(--brand-green)]" />
                <h3 className="mt-4 text-xl font-semibold">Your email app should open now.</h3>
                <p className="mt-2 text-muted-foreground text-sm">If it doesn’t, send your message to business@zenisoft.com and we’ll respond within one business day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="rounded-xl border border-border p-6 space-y-4">
                <div aria-hidden="true" className="hidden">
                  <label>Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name" name="name" required error={errors.name} />
                  <Field label="Company" name="company" error={errors.company} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email" name="email" type="email" required error={errors.email} />
                  <Field label="Phone" name="phone" error={errors.phone} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">What do you need?<span className="text-[var(--brand-green)]">*</span></label>
                  <textarea name="message" rows={5} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)]" />
                  {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
                </div>
                {serverError && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {serverError}
                  </div>
                )}
                <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[var(--brand-navy)] px-5 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? "Opening email app..." : "Send message"}
                </button>
                <p className="text-xs text-muted-foreground">We'll respond within one business day. No spam, ever.</p>
              </form>
            )}
          </div>
        </div>
      </Section>
    </SiteLayout>
  );
}

function Field({ label, name, type = "text", required, error }: { label: string; name: string; type?: string; required?: boolean; error?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}{required && <span className="text-[var(--brand-green)]">*</span>}</label>
      <input name={name} type={type} className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-green)] ${error ? "border-destructive" : "border-input"}`} />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

